import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import PropTypes, { object } from 'prop-types'
import { useHistory } from 'react-router-dom'

import InfoBox from 'components/Shared/InfoBox'
import { formatEventSubheadlineLocation, numberWithThousandsSeparators } from 'utils/helpers'
import { Accordion, Card } from 'react-bootstrap'
import { ClockIcon, PinIcon, VenueIcon } from 'images'
import moment from 'moment'
import EventStatsPDF from '../EventStatsPDF'
import { EventCampaignResultsContext, EventFbInsightsContext } from './EventStatsProvider'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import EventStatsOverview from './EventStatsOverview'
import { isCampaignRunning } from 'api/models'

const EventCard = ({ event }) => {
    const history = useHistory()

    const redirectUrl = () => {
        if(window.innerWidth > 991) {
            history.replace(`/events/?drawer-open=true&event-id=${event.id}&tab=1`)
        } else history.replace(`/events/${event.id}`)
        window.location.reload()
    }

    return <div className='event-card' key={event.id} onClick={redirectUrl}>
        <div className="event-row_left-content_title-and-date_wrapper m-0">
            <div className="event-row_left-content_title-and-date_title d-block">
                {event.title}
            </div>
            <div className="event-row_left-content_title-and-date_date_wrapper d-flex">
                <span className="event-row_left-content_title-and-date_date_time">
                    <span>
                        <ClockIcon className='mr-1' height={12} width={17} />
                    </span>
                    <time dateTime={moment.utc(event.start_date_time).format('DD.MM.YYYY, HH:mm')}>
                        {moment.utc(event.start_date_time).format('DD.MM.YYYY, HH:mm')}
                    </time>
                </span>
                <span className='event-row_left-content_title-and-date_date_location'>
                    <span>
                        <PinIcon className='mr-1' height={12} width={17} />
                    </span>
                    <span>{
                        formatEventSubheadlineLocation({
                            city: event.city
                        })
                    }</span>
                </span>
                <span className='event-row_left-content_title-and-date_date_location'>
                    <span>
                        <VenueIcon className='mr-1' height={12} width={17} />
                    </span>
                    <span>{
                        formatEventSubheadlineLocation({
                            hall: event.hall_name,
                            venue: event.venue_name
                        })
                    }</span>
                </span>
            </div>
        </div>
    </div>
}

EventCard.propTypes = {
    event: PropTypes.object
}

const EventStatistics = ({ event, eventSeries, campaigns = [], isSideDrawer = false }) => {
    const { t } = useTranslation()
    const [activeKey, setActiveKey] = useState(null)
    const user = useSelector(store => store.user)
    const [userCurrency, setUserCurrency] = useState('')
    const [isShortCampaign, setIsShortCampaign] = useState(false)
    const [adCreatives, setAdCreatives] = useState({ totalAds: 0, adSuggestions: { regular: [], reels: [] }})
    const campaignResults = useContext(EventCampaignResultsContext)
    const fbInsights = useContext(EventFbInsightsContext)
    const runningCampaigns = campaigns.filter(campaign => isCampaignRunning(campaign))
    const includedEventsRef = useRef(null)
    const openIncludedEvents = new URL(window.location.href).searchParams.get('open_included_events')

    useEffect(() => {
        const currency = localStorage.getItem('userCurrency')
        if (currency) {
            setUserCurrency(currency)
        }
    }, [])

    useEffect(() => {
        if(campaignResults.error && campaignResults.message === 'Campaign timeframe too low to evaluate!') {
            setIsShortCampaign(true)
        }
    }, [campaignResults])

    const eventStats = campaignResults.content?.event_results
    const eventSeriesStats = campaignResults.content?.series_results
    const currency = userCurrency || 'CHF'
    const filteredEventSeries = (eventSeries?.series || []).filter(el => el.id !== event.id)
    const numOfTCs = runningCampaigns.length

    openIncludedEvents && includedEventsRef.current && includedEventsRef.current.scrollIntoView(true)

    return <div className='d-flex flex-column gap-4 mt-4'>

        <EventStatsOverview
            isSideDrawer={isSideDrawer}
            fbInsights={fbInsights}
            currency={currency}
            runningCampaigns={runningCampaigns}
            setAdCreatives={setAdCreatives}
        />

        <div className='position-relative'>
            <div className={`${isShortCampaign ? 'event-series-stats_disabled-card' : ''}`}>
                <div className='row py-md-4'>
                    <div className='col-md-6 col-12'>
                        <InfoBox content='Dashboard.eventSeriesStats.purchases' body={t('Dashboard.eventSeriesStats.purchases.description')}>
                            <h2 className="card-title-default d-inline-block mb-0">
                                {t('Dashboard.eventSeriesStats.purchases.title')}
                            </h2>
                        </InfoBox>

                        <div className='event-series-stats_data'>
                            <div className='event event_single'>
                                <span className='bold'>{numberWithThousandsSeparators(eventStats?.total_transactions)}</span>
                                <span>{t('Dashboard.eventSeriesStats.singleEvent')}</span>
                            </div>
                            <div className='event event_series'>
                                <span className='bold'>{numberWithThousandsSeparators(eventSeriesStats?.total_transactions)}</span>
                                <span>{t('Dashboard.eventSeriesStats.eventSeries')}</span>
                            </div>
                        </div>
                    </div>
                    <div className='col-md-6 col-12'>
                        <InfoBox content='Dashboard.eventSeriesStats.adSpendPerTicket' body={t('Dashboard.eventSeriesStats.adSpendPerTicket.description')}>
                            <h2 className="card-title-default d-inline-block mb-0">
                                {t('Dashboard.eventSeriesStats.adSpendPerTicket.title')}
                            </h2>
                        </InfoBox>
                        <div className='event-series-stats_data'>
                            <div className='event event_single'>
                                <span className='bold'>{numberWithThousandsSeparators(eventStats?.cpo, { currency, maxFractionDigits: 0 })}</span>
                                <span>{t('Dashboard.eventSeriesStats.singleEvent')}</span>
                            </div>
                            <div className='event event_series'>
                                <span className='bold'>{numberWithThousandsSeparators(eventSeriesStats?.cpo, { currency, maxFractionDigits: 0 })}</span>
                                <span>{t('Dashboard.eventSeriesStats.eventSeries')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-6 col-12'>
                        <InfoBox content='Dashboard.eventSeriesStats.revenue' body={t('Dashboard.eventSeriesStats.revenue.description')}>
                            <h2 className="card-title-default d-inline-block mb-0">
                                {t('Dashboard.eventSeriesStats.revenue.title')}
                            </h2>
                        </InfoBox>
                        <div className='event-series-stats_data'>
                            <div className='event event_single'>
                                <span className='bold'>{numberWithThousandsSeparators(eventStats?.total_revenue, { currency, maxFractionDigits: 0 })}</span>
                                <span>{t('Dashboard.eventSeriesStats.singleEvent')}</span>
                            </div>
                            <div className='event event_series'>
                                <span className='bold'>{numberWithThousandsSeparators(eventSeriesStats?.total_revenue, { currency, maxFractionDigits: 0 })}</span>
                                <span>{t('Dashboard.eventSeriesStats.eventSeries')}</span>
                            </div>
                        </div>
                    </div>
                    <div className='col-md-6 col-12'>
                        <InfoBox content='Dashboard.eventSeriesStats.roi' body={t('Dashboard.eventSeriesStats.roi.description')}>
                            <h2 className="card-title-default d-inline-block mb-0">
                                {t('Dashboard.eventSeriesStats.roi.title')}
                            </h2>
                        </InfoBox>
                        <div className='event-series-stats_data'>
                            <div className='event event_single'>
                                <span className='bold'>{numberWithThousandsSeparators(eventStats?.roas, { maxFractionDigits: 0, suffix: '%' })}</span>
                                <span>{t('Dashboard.eventSeriesStats.singleEvent')}</span>
                            </div>
                            <div className='event event_series'>
                                <span className='bold'>{numberWithThousandsSeparators(eventSeriesStats?.roas, { maxFractionDigits: 0, suffix: '%' })}</span>
                                <span>{t('Dashboard.eventSeriesStats.eventSeries')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {isShortCampaign && <div className='event-series-stats disabled-overlay'>
                <TipDialogueBox
                    title={t('Dashboard.eventSeriesStats.runtimeAlert.title')}
                    text={t('Dashboard.eventSeriesStats.runtimeAlert.description')}
                    variant="danger"
                    showBorder
                    compact
                />
            </div>}
        </div>

        <div className={`event-series-stats_event-list ${isShortCampaign ? 'event-series-stats_disabled-card' : ''}`} id="campaigns-container">
            {!!filteredEventSeries.length && <Accordion ref={includedEventsRef} defaultActiveKey={openIncludedEvents ? '0' : null}>
                <Card className='goals-card'>
                    <Accordion.Toggle eventKey='0'>
                        <Card.Header
                            as={Card.Header}
                            className={classNames({
                                'goals-card_header pt-3': true,
                                'open': activeKey === '0' || openIncludedEvents
                            })}
                            onClick={() => setActiveKey((activeKey && activeKey === '0') || openIncludedEvents ? null : '0')}
                        >
                            <InfoBox withAnchor={false}>
                                {t('Dashboard.eventSeriesStats.includedEvents')}
                            </InfoBox>
                            <div className='align-self-center ml-auto'>
                                <div className={`accordion-arrow ${(activeKey === '0' || openIncludedEvents) ? 'open' : ''}`} />
                            </div>
                        </Card.Header>
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey='0'>
                        <Card.Body >
                            <div className='d-flex flex-column p-0'>
                                {filteredEventSeries.map(el => {
                                    return <EventCard event={el} key={el.id} />
                                })}
                            </div>
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
            </Accordion>}

            {event.title && <PDFDownloadLink
                document={<EventStatsPDF 
                    fbInsights={fbInsights}
                    eventStats={eventStats}
                    eventSeriesStats={eventSeriesStats}
                    eventSeries={filteredEventSeries}
                    eventInfo={event}
                    user={user}
                    numOfTCs={numOfTCs}
                    adCreatives={adCreatives}
                />}
                fileName={`${event.title}_campaign-stats_${moment().format('DD-MM-yyyy')}.pdf`}
            >
                {({ loading }) =>
                    loading
                        ? t('Event.statistics.generatingDocument')
                        : <button
                            style={{ width: '100%' }}
                            className='bt s-38 bt-orange'
                        >
                            {t('Dashboard.eventSeriesStats.save')}
                        </button>
                }
            </PDFDownloadLink>}
        </div>
    </div>
}

EventStatistics.propTypes = {
    event: PropTypes.object.isRequired,
    eventSeries: PropTypes.object.isRequired,
    campaigns: PropTypes.arrayOf(object),
    isSideDrawer: PropTypes.bool,
}

export default EventStatistics
